import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import axios from "axios";
import MarketingHeader from "@/components/layout/MarketingHeader";
import MarketingFooter from "@/components/layout/MarketingFooter";
import { formatMoney, formatDate } from "@/lib/api";

// Public, unauthenticated view of a shared Next Tight Week snapshot.
// Renders as a full page with the marketing chrome so the link preview
// (OG meta tags on the app) still reads on-brand when someone lands here.
// No login required — anyone with the URL can view.
export default function SnapshotView() {
  const { snapId } = useParams();
  const [snap, setSnap] = useState(null);
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    (async () => {
      try {
        const base = process.env.REACT_APP_BACKEND_URL;
        const { data } = await axios.get(`${base}/api/snapshots/${snapId}`);
        setSnap(data);
      } catch (e) {
        setError(e?.response?.data?.detail || "Snapshot not found");
      } finally {
        setLoading(false);
      }
    })();
  }, [snapId]);

  return (
    <div className="min-h-screen bg-[#EFE9DE]" data-testid="snapshot-view">
      <MarketingHeader />
      <main className="max-w-3xl mx-auto px-6 md:px-8 py-12 md:py-16">
        {loading && (
          <div className="text-[#59605C] text-sm" data-testid="snapshot-loading">
            Loading snapshot…
          </div>
        )}
        {error && (
          <div className="card-soft p-8" data-testid="snapshot-error">
            <div className="text-[10px] font-semibold uppercase tracking-widest text-[#8A918D]">
              Snapshot unavailable
            </div>
            <h1 className="font-heading text-2xl text-[#2C302E] mt-2">
              {error === "Snapshot expired" ? "This link has expired" : "We couldn't find that snapshot"}
            </h1>
            <p className="text-[#59605C] mt-3 text-sm leading-relaxed">
              Shared snapshots are only valid for 30 days. Ask the person who
              sent it to share a fresh one — or start your own free forecast to
              see what your paychecks will cover.
            </p>
            <Link to="/" data-testid="snapshot-error-cta" className="btn-primary mt-6 inline-flex">
              Try WhatsYourBalance.com →
            </Link>
          </div>
        )}
        {snap && <SnapshotCard snap={snap} />}
      </main>
      <MarketingFooter />
    </div>
  );
}

// Big display card for the shared week. Amber/red tint to match the "tight
// week" language, plus the note the sender left. CTAs at the bottom nudge
// the recipient toward signing up if they want their own forecast.
function SnapshotCard({ snap }) {
  const p = snap.payload || {};
  const isShort = p.status === "short";
  const isTight = p.status === "tight";
  const bg = isShort ? "bg-[#FEF3EE] border-[#F0C8B8]" : isTight ? "bg-[#FFF9EA] border-[#E9D69F]" : "bg-[#EEF3EC] border-[#B8CFB1]";
  const accent = isShort ? "text-[#7A2E24]" : isTight ? "text-[#7A4E1F]" : "text-[#3B5B3E]";
  const dot = isShort ? "bg-[#8C3A30]" : isTight ? "bg-[#B48A24]" : "bg-[#3B5B3E]";
  const label = isShort ? "Short week ahead" : isTight ? "Tight week ahead" : "Safe week";
  return (
    <div data-testid="snapshot-card">
      <div className="text-xs text-[#8A918D] uppercase tracking-widest">
        Shared by {snap.creator_name || "a friend"}
      </div>
      <h1 className="font-heading text-3xl md:text-4xl font-medium text-[#2C302E] mt-2 leading-tight">
        Heads up — {isShort || isTight ? "a bumpy week is coming" : "a week to plan for"}
      </h1>

      <div className={`mt-6 rounded-2xl border p-6 md:p-8 ${bg}`} data-testid="snapshot-week-card">
        <div className="flex items-center gap-2 flex-wrap">
          <span className={`w-2.5 h-2.5 rounded-full ${dot}`} />
          <span className={`text-[11px] font-semibold uppercase tracking-widest ${accent}`}>{label}</span>
        </div>
        <div className={`mt-2 font-heading text-2xl md:text-3xl font-semibold ${accent}`} data-testid="snapshot-week-range">
          {formatDate(p.week_start)} → {formatDate(p.week_end)}
        </div>
        <div className="mt-6 grid sm:grid-cols-2 gap-4">
          <div className="rounded-xl bg-white/70 p-4">
            <div className="text-[10px] font-semibold uppercase tracking-widest text-[#8A918D]">Projected end-of-week balance</div>
            <div className={`font-heading text-3xl font-semibold mt-1 ${p.end_balance < 0 ? "text-[#8C3A30]" : "text-[#3B5B3E]"}`} data-testid="snapshot-balance">
              {formatMoney(p.end_balance)}
            </div>
          </div>
          <div className="rounded-xl bg-white/70 p-4">
            <div className="text-[10px] font-semibold uppercase tracking-widest text-[#8A918D]">Bills due that week</div>
            <div className="font-heading text-3xl font-semibold mt-1 text-[#2C302E]" data-testid="snapshot-bill-count">
              {p.bill_count || 0}
            </div>
          </div>
        </div>
        {p.note && (
          <div className="mt-6 rounded-xl bg-white/70 p-4 border border-[#D8CFB9]" data-testid="snapshot-note">
            <div className="text-[10px] font-semibold uppercase tracking-widest text-[#8A918D]">Note</div>
            <p className="text-sm text-[#2C302E] leading-relaxed mt-1">{p.note}</p>
          </div>
        )}
      </div>

      <div className="mt-8 card-soft p-6">
        <h2 className="font-heading text-lg text-[#2C302E]">Want your own forecast?</h2>
        <p className="text-sm text-[#59605C] mt-2 leading-relaxed">
          WhatsYourBalance.com is a calmer way to see if your paycheck covers
          your bills — no spreadsheets, no budget categories. One month free,
          no card required.
        </p>
        <div className="mt-5 flex flex-wrap gap-3">
          <Link to="/signup" data-testid="snapshot-signup-cta" className="btn-primary">Start your free month →</Link>
          <Link to="/" data-testid="snapshot-learn-cta" className="btn-secondary">How it works</Link>
        </div>
      </div>

      <div className="mt-8 text-[11px] text-[#8A918D]">
        Snapshot created {snap.created_at ? formatDate(snap.created_at.slice(0, 10)) : ""} · Links auto-expire after 30 days.
      </div>
    </div>
  );
}
